import React from 'react'
import ElegantTemplate from './ElegantTemplate'
import ModernTemplate from './ModernTemplate'
import ConfiableTemplate from './ConfiableTemplate'
import DinamicoTemplate from './DinamicoTemplate'
import OrbeTemplate from './OrbeTemplate'
import ShowcaseTemplate from './ShowcaseTemplate'
import MinimalistaTemplate from './MinimalistaTemplate'
import NaturalTemplate from './NaturalTemplate'
import CorporativoTemplate from './CorporativoTemplate'
import OrbeAgencyTemplate from './OrbeAgencyTemplate'

/**
 * index.ts — Registro central de templates
 *
 * ARQUITECTURA DE ACCESO POR PLAN:
 * El acceso a templates YA NO se define aquí con requiredPlan[].
 * La fuente de verdad es planLimits.ts → templates_available por plan.
 * getTemplatesByPlan() consulta esa fuente via API (/api/agents/plan-limits).
 *
 * ¿Por qué se eliminó requiredPlan hardcodeado?
 * - Había 3 fuentes desincronizadas: index.ts, planLimits.ts, Settings.tsx
 * - Al agregar un plan nuevo había que actualizar los 3 lugares
 * - Settings.tsx mostraba "Requiere Básico" a agentes Smart aunque tenían acceso
 *
 * Para agregar un template nuevo:
 *   1. Crear el componente .tsx en esta carpeta
 *   2. Registrarlo en templateOptions abajo
 *   3. Agregar su id en planLimits.ts → templates_available de los planes que apliquen
 *   4. Agregar su accentColor en TEMPLATE_ACCENT (Settings.tsx)
 *   NO modificar requiredPlan — ese campo ya no existe
 *
 * Templates privados (isPrivate + privateForAgency):
 *   Siguen funcionando igual — acceso por agencia, no por plan
 */

export interface TemplateOption {
  id: string
  name: string
  description: string
  personality: string
  emotions: string[]
  idealFor: string[]
  colorScheme: string
  preview: string
  component: React.ComponentType<any>
  isPrivate?: boolean
  privateForAgency?: string
}

export const templateOptions: TemplateOption[] = [
  {
    id: 'showcase',
    name: 'Showcase',
    description: 'Tu vitrina profesional para empezar a brillar',
    personality: 'Limpio, impactante y enfocado en convertir visitantes en contactos.',
    emotions: ['Curiosidad', 'Confianza', 'Profesionalismo'],
    idealFor: [
      'Agentes que inician su presencia digital',
      'Primeros pasos en el mercado inmobiliario',
    ],
    colorScheme: 'Azul índigo y blanco',
    preview: '/previews/showcase.jpg',
    component: ShowcaseTemplate,
  },
  {
    id: 'elegante',
    name: 'Elegante',
    description: 'Sofisticación y profesionalismo que inspira confianza',
    personality: 'Profesional, refinado y confiable. Para agentes que valoran la tradición y la elegancia clásica.',
    emotions: ['Confianza', 'Respeto', 'Seguridad', 'Sofisticación'],
    idealFor: [
      'Agentes con experiencia consolidada',
      'Mercado residencial de alta gama',
      'Clientes que buscan seriedad',
      'Zonas tradicionales y prestigiosas',
    ],
    colorScheme: 'Grises, blancos y acentos dorados',
    preview: '/previews/elegante.jpg',
    component: ElegantTemplate,
  },
  {
    id: 'moderno',
    name: 'Moderno',
    description: 'Energía innovadora que conecta con la nueva generación',
    personality: 'Vanguardista, tech-savvy y dinámico. Para agentes que abrazan la innovación.',
    emotions: ['Emoción', 'Innovación', 'Energía', 'Futuro'],
    idealFor: [
      'Agentes jóvenes y tecnológicos',
      'Mercado millennial y Gen Z',
      'Propiedades modernas y smart homes',
      'Zonas urbanas y desarrollos nuevos',
    ],
    colorScheme: 'Púrpuras, azules y gradientes vibrantes',
    preview: '/previews/moderno.jpg',
    component: ModernTemplate,
  },
  {
    id: 'confiable',
    name: 'Confiable',
    description: 'Calidez familiar que transmite seguridad y cercanía',
    personality: 'Cálido, familiar y accesible. Para agentes que priorizan las relaciones personales.',
    emotions: ['Tranquilidad', 'Calidez', 'Familiaridad', 'Protección'],
    idealFor: [
      'Agentes enfocados en familias',
      'Primera vivienda y hogares familiares',
      'Clientes que buscan confianza personal',
      'Comunidades residenciales establecidas',
    ],
    colorScheme: 'Ámbar, naranjas y tonos cálidos',
    preview: '/previews/confiable.jpg',
    component: ConfiableTemplate,
  },
  {
    id: 'dinamico',
    name: 'Dinámico',
    description: 'Energía explosiva que motiva y genera resultados rápidos',
    personality: 'Enérgico, ambicioso y orientado a resultados. Para agentes competitivos.',
    emotions: ['Adrenalina', 'Motivación', 'Urgencia', 'Ambición'],
    idealFor: [
      'Agentes competitivos y ambiciosos',
      'Mercado de inversión y oportunidades',
      'Clientes que buscan rapidez',
      'Zonas comerciales y de alto movimiento',
    ],
    colorScheme: 'Rojos, naranjas y amarillos intensos',
    preview: '/previews/dinamico.jpg',
    component: DinamicoTemplate,
  },
  {
    id: 'minimalista',
    name: 'Minimalista',
    description: 'Elegancia silenciosa para el mercado premium',
    personality: 'Ultra limpio, tipografía protagonista y espacios en blanco que comunican exclusividad.',
    emotions: ['Exclusividad', 'Sofisticación', 'Claridad', 'Distinción'],
    idealFor: [
      'Mercado residencial premium y luxury',
      'Propiedades de alto valor',
      'Clientes que aprecian el diseño',
      'Agentes con portafolio selecto',
    ],
    colorScheme: 'Blanco, negro y tipografía protagonista',
    preview: '/previews/minimalista.jpg',
    component: MinimalistaTemplate,
  },
  {
    id: 'natural',
    name: 'Natural',
    description: 'Conexión auténtica con la tierra y el entorno',
    personality: 'Orgánico, sustentable y cercano. Para propiedades con carácter y agentes con valores.',
    emotions: ['Serenidad', 'Autenticidad', 'Conexión', 'Bienestar'],
    idealFor: [
      'Desarrollos eco-friendly y sustentables',
      'Propiedades en zonas turísticas o naturales',
      'Agentes con enfoque en calidad de vida',
      'Mercado de casas de campo y playa',
    ],
    colorScheme: 'Verdes, tierra y beige orgánico',
    preview: '/previews/natural.jpg',
    component: NaturalTemplate,
  },
  {
    id: 'corporativo',
    name: 'Corporativo',
    description: 'Presencia institucional para el mercado B2B',
    personality: 'Formal, estructurado y orientado a resultados. Para inmobiliarias y mercado comercial.',
    emotions: ['Autoridad', 'Confianza', 'Eficiencia', 'Solidez'],
    idealFor: [
      'Inmobiliarias y agencias establecidas',
      'Mercado de oficinas y locales comerciales',
      'Naves industriales y bodegas',
      'Clientes corporativos e inversores',
    ],
    colorScheme: 'Azul marino, gris carbón y blanco institucional',
    preview: '/previews/corporativo.jpg',
    component: CorporativoTemplate,
  },
  {
    id: 'orbe',
    name: 'Orbe Bienes Raíces',
    description: 'Template exclusivo para agentes de Orbe Bienes Raíces',
    personality: 'Institucional, profesional y elegante. Branding corporativo de Orbe.',
    emotions: ['Confianza', 'Profesionalismo', 'Elegancia', 'Tradición'],
    idealFor: ['Agentes de Orbe Bienes Raíces exclusivamente'],
    colorScheme: 'Azul marino y dorado',
    preview: '/previews/orbe.jpg',
    component: OrbeTemplate,
    isPrivate: true,
    privateForAgency: 'orbe',
  },
  {
    id: 'orbe-agencia',
    name: 'Orbe Agencia',
    description: 'Template principal de la agencia Orbe',
    personality: 'Institucional, sobrio, profesional',
    emotions: ['Confianza', 'Exclusividad'],
    idealFor: ['Agencia Orbe'],
    colorScheme: 'Dorado y azul marino',
    preview: '',
    component: OrbeAgencyTemplate,
    isPrivate: true,
    privateForAgency: 'orbe',
  },
]

// ─────────────────────────────────────────────────────────────
// getTemplatesByPlan
//
// Devuelve templates accesibles para un agente según su plan.
// Fuente de verdad: planLimits.ts → templates_available
// El frontend recibe los IDs permitidos via /api/agents/plan-limits
//
// Para templates privados de agencia (isPrivate + privateForAgency):
//   Solo visibles si el agente pertenece a esa agencia.
//   Estos NO dependen de plan — son custom Elite asignados manualmente.
//
// Normalización de planes legacy:
//   trialing → free | basico → smart
// ─────────────────────────────────────────────────────────────
export const getTemplatesByPlan = (
  plan: string,
  subdomain?: string,
  parentSubdomain?: string,
  allowedTemplateIds?: string[]
): TemplateOption[] => {
  // Normalizar plan legacy
  let normalizedPlan = plan?.toLowerCase() || 'free'
  if (normalizedPlan === 'trialing') normalizedPlan = 'free'
  if (normalizedPlan === 'basico')   normalizedPlan = 'smart'

  // IDs permitidos por plan — viene de planLimits.templates_available
  // Si no se pasan, usamos el mapeo local como fallback (mientras carga la API)
  const FALLBACK_BY_PLAN: Record<string, string[]> = {
    free:    ['showcase'],
    starter: ['showcase', 'elegante', 'moderno'],
    smart:   ['showcase', 'elegante', 'moderno', 'confiable', 'dinamico'],
    pro:     ['showcase', 'elegante', 'moderno', 'confiable', 'dinamico', 'minimalista', 'natural', 'corporativo'],
    elite:   ['*'],
  }

  const permitted = allowedTemplateIds ?? FALLBACK_BY_PLAN[normalizedPlan] ?? ['showcase']
  const isElite   = permitted.includes('*')

  return templateOptions.filter(template => {
    // Templates privados de agencia
    if (template.isPrivate && template.privateForAgency) {
      const slug = template.privateForAgency
      return subdomain?.includes(slug) || parentSubdomain?.includes(slug)
    }

    // Templates privados sin agencia: nunca en selector general
    if (template.isPrivate) return false

    // Elite tiene acceso a todos los públicos
    if (isElite) return true

    return permitted.includes(template.id)
  })
}

// Templates públicos (sin privados) — para landing de marketing y referencias
export const publicTemplateOptions = templateOptions.filter(t => !t.isPrivate)

// Obtener template por id con fallback a showcase
export const getTemplateById = (id: string): TemplateOption =>
  templateOptions.find(t => t.id === id) || templateOptions[0]

// Obtener componente React por id
// Si el agente tiene un template fuera de su plan (edge case post-downgrade),
// se respeta el template actual — solo se restringe la selección, no el rendering
export const getTemplateComponent = (templateId: string): React.ComponentType<any> =>
  getTemplateById(templateId).component

// planHasTemplate — helper para validar desde backend o guards futuros
export const planHasTemplate = (
  plan: string,
  templateId: string,
  subdomain?: string,
  parentSubdomain?: string
): boolean => {
  const available = getTemplatesByPlan(plan, subdomain, parentSubdomain)
  return available.some(t => t.id === templateId)
}

export {
  ShowcaseTemplate,
  ElegantTemplate,
  ModernTemplate,
  ConfiableTemplate,
  DinamicoTemplate,
  MinimalistaTemplate,
  NaturalTemplate,
  CorporativoTemplate,
  OrbeTemplate,
}
